import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { useLanguage } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  ArrowLeft, 
  Mail, 
  Calendar,
  Clock,
  Send,
  Trash2,
  Plus,
  Settings,
  CheckCircle,
  AlertCircle,
  Loader2
} from "lucide-react";

const DAYS_OF_WEEK = [
  { value: "0", key: "monday" },
  { value: "1", key: "tuesday" },
  { value: "2", key: "wednesday" },
  { value: "3", key: "thursday" },
  { value: "4", key: "friday" },
  { value: "5", key: "saturday" },
  { value: "6", key: "sunday" },
];

const HOURS = Array.from({ length: 24 }, (_, i) => ({
  value: String(i),
  label: `${String(i).padStart(2, '0')}:00`
}));

export default function WeeklyReportPage({ evaluator }) {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [settings, setSettings] = useState({
    enabled: false,
    recipients: [],
    day_of_week: 0,
    hour: 8,
    last_sent: null
  });
  const [newEmail, setNewEmail] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/weekly-report/settings`);
      setSettings(response.data);
    } catch (error) {
      toast.error("Erro ao carregar configurações");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSaveSettings = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API}/weekly-report/settings`, {
        enabled: settings.enabled,
        recipients: settings.recipients,
        day_of_week: settings.day_of_week,
        hour: settings.hour
      });
      toast.success("Configurações salvas com sucesso!");
    } catch (error) {
      toast.error("Erro ao salvar configurações");
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddEmail = () => {
    if (!newEmail || !newEmail.includes("@")) {
      toast.error("Digite um e-mail válido");
      return;
    }
    if (settings.recipients.includes(newEmail)) {
      toast.error("E-mail já adicionado");
      return;
    }
    setSettings({
      ...settings,
      recipients: [...settings.recipients, newEmail]
    });
    setNewEmail("");
  };

  const handleRemoveEmail = (email) => {
    setSettings({
      ...settings,
      recipients: settings.recipients.filter(e => e !== email)
    });
  };

  const handleSendNow = async () => {
    if (settings.recipients.length === 0) {
      toast.error("Adicione pelo menos um destinatário");
      return;
    }
    
    setIsSending(true);
    try {
      const response = await axios.post(`${API}/weekly-report/send`);
      toast.success(response.data.message);
      fetchSettings(); // Refresh to get updated last_sent
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao enviar relatório");
    } finally {
      setIsSending(false);
    }
  };

  const handlePreview = async () => {
    try {
      const response = await axios.get(`${API}/weekly-report/preview`);
      setPreviewData(response.data.data);
    } catch (error) {
      toast.error("Erro ao carregar preview");
    }
  };

  const formatLastSent = (dateStr) => {
    if (!dateStr) return "Nunca enviado";
    const date = new Date(dateStr);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (evaluator?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="bg-card/50 border-destructive">
          <CardContent className="p-6 text-center">
            <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <p className="text-lg font-semibold">Acesso Restrito</p>
            <p className="text-muted-foreground">Apenas administradores podem acessar esta página.</p>
            <Button onClick={() => navigate('/dashboard')} className="mt-4">
              Voltar ao Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/dashboard')}
                data-testid="back-btn"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-xl md:text-2xl font-bold tracking-tight">
                  Relatório Semanal
                </h1>
                <p className="text-sm text-muted-foreground">
                  Configure o envio automático de relatórios por e-mail
                </p>
              </div>
            </div>
            <Badge variant={settings.enabled ? "default" : "secondary"}>
              {settings.enabled ? t('weeklyReport.enabled') : t('weeklyReport.disabled')}
            </Badge>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="grid gap-6 md:grid-cols-2">
          {/* Settings Card */}
          <Card className="bg-card/50 border-border/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5 text-primary" />
                Configurações
              </CardTitle>
              <CardDescription>
                Defina quando e para quem enviar o relatório
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Enable/Disable */}
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base">Envio Automático</Label>
                  <p className="text-sm text-muted-foreground">
                    Enviar relatório automaticamente toda semana
                  </p>
                </div>
                <Switch
                  checked={settings.enabled}
                  onCheckedChange={(checked) => setSettings({ ...settings, enabled: checked })}
                  data-testid="enable-switch"
                />
              </div>

              {/* Day of Week */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Dia da Semana
                </Label>
                <Select
                  value={String(settings.day_of_week)}
                  onValueChange={(value) => setSettings({ ...settings, day_of_week: parseInt(value) })}
                >
                  <SelectTrigger data-testid="day-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DAYS_OF_WEEK.map((day) => (
                      <SelectItem key={day.value} value={day.value}>
                        {t(`weeklyReport.${day.key}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Hour */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Horário
                </Label>
                <Select
                  value={String(settings.hour)}
                  onValueChange={(value) => setSettings({ ...settings, hour: parseInt(value) })}
                >
                  <SelectTrigger data-testid="hour-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {HOURS.map((hour) => (
                      <SelectItem key={hour.value} value={hour.value}>
                        {hour.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Last Sent */}
              <div className="pt-4 border-t border-border/50">
                <p className="text-sm text-muted-foreground">
                  Último envio: <span className="text-foreground">{formatLastSent(settings.last_sent)}</span>
                </p>
              </div>

              <Button 
                onClick={handleSaveSettings} 
                className="w-full"
                disabled={isSaving}
                data-testid="save-settings-btn"
              >
                {isSaving ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <CheckCircle className="h-4 w-4 mr-2" />
                )}
                Salvar Configurações
              </Button>
            </CardContent>
          </Card>

          {/* Recipients Card */}
          <Card className="bg-card/50 border-border/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-primary" />
                Destinatários
              </CardTitle>
              <CardDescription>
                E-mails que receberão o relatório semanal
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Warning when no recipients */}
              {settings.recipients.length === 0 && (
                <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg mb-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-yellow-500">Nenhum destinatário configurado</p>
                      <p className="text-sm text-yellow-500/80 mt-1">
                        Adicione pelo menos um e-mail para poder enviar os relatórios semanais.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Add Email */}
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="novo@email.com"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddEmail()}
                  className="bg-input/50"
                  data-testid="new-email-input"
                />
                <Button onClick={handleAddEmail} size="icon" data-testid="add-email-btn">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              {/* Email List */}
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {settings.recipients.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    Nenhum destinatário adicionado
                  </p>
                ) : (
                  settings.recipients.map((email) => (
                    <div
                      key={email}
                      className="flex items-center justify-between p-3 bg-input/30 rounded-lg"
                    >
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">{email}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => handleRemoveEmail(email)}
                        data-testid={`remove-email-${email}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))
                )}
              </div>

              {/* Send Now Button */}
              <div className="pt-4 border-t border-border/50">
                <Button
                  onClick={handleSendNow}
                  variant="outline"
                  className="w-full"
                  disabled={isSending || settings.recipients.length === 0}
                  data-testid="send-now-btn"
                >
                  {isSending ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  Enviar Relatório Agora
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Preview Card */}
          <Card className="bg-card/50 border-border/50 md:col-span-2">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Preview do Relatório</CardTitle>
                  <CardDescription>
                    Veja como será o relatório enviado por e-mail
                  </CardDescription>
                </div>
                <Button onClick={handlePreview} variant="outline" data-testid="preview-btn">
                  Carregar Preview
                </Button>
              </div>
            </CardHeader>
            {previewData && (
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-input/30 p-4 rounded-lg text-center">
                    <p className="text-xs text-muted-foreground uppercase">Jogadores</p>
                    <p className="text-2xl font-bold">{previewData.total_players}</p>
                  </div>
                  <div className="bg-input/30 p-4 rounded-lg text-center">
                    <p className="text-xs text-muted-foreground uppercase">Avaliações</p>
                    <p className="text-2xl font-bold">{previewData.total_evaluations}</p>
                  </div>
                  <div className="bg-input/30 p-4 rounded-lg text-center">
                    <p className="text-xs text-muted-foreground uppercase">Média Geral</p>
                    <p className="text-2xl font-bold text-green-400">{previewData.overall_average?.toFixed(1)}</p>
                  </div>
                  <div className="bg-input/30 p-4 rounded-lg text-center">
                    <p className="text-xs text-muted-foreground uppercase">Novas (7 dias)</p>
                    <p className="text-2xl font-bold text-primary">{previewData.new_evaluations_count}</p>
                  </div>
                </div>

                {/* Top 5 */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    🏆 Top 5 Ranking
                  </h3>
                  <div className="space-y-2">
                    {previewData.top_players?.slice(0, 5).map((player, i) => (
                      <div
                        key={player.name || `top-${i}`}
                        className="flex items-center justify-between p-3 bg-input/20 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <span className={`font-bold ${i === 0 ? 'text-yellow-400' : i === 1 ? 'text-gray-300' : i === 2 ? 'text-amber-600' : 'text-muted-foreground'}`}>
                            {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}º`}
                          </span>
                          <span>{player.name}</span>
                          <Badge variant="outline">{player.position}</Badge>
                        </div>
                        <span className="font-bold text-green-400">{player.average?.toFixed(1)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Needs Evaluation */}
                {previewData.needs_evaluation?.length > 0 && (
                  <div className="bg-yellow-500/10 border border-yellow-500/30 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold mb-3 text-yellow-400 flex items-center gap-2">
                      ⚠️ Precisam de Avaliação ({previewData.needs_evaluation_total})
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {previewData.needs_evaluation.slice(0, 5).map((player) => (
                        <Badge key={player.player_name || player.player_id} variant="outline" className="border-yellow-500/50">
                          {player.player_name}
                        </Badge>
                      ))}
                      {previewData.needs_evaluation_total > 5 && (
                        <Badge variant="secondary">+{previewData.needs_evaluation_total - 5} mais</Badge>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            )}
          </Card>
        </div>
      </main>
    </div>
  );
}
